import { useMemo, useState, useEffect } from 'react';
import { computeGroupStats, computeTypeStats } from '../utils/albumStats';

const COLLAPSED_KEY = 'albumStatsCharts_collapsed';

// Paleta alineada al tema dorado de la app. El verde brillante #4ade80
// que usábamos antes era demasiado vibrante y desentonaba con el resto
// de la UI — lo reemplazamos por un sage (#9ec5a8) más suave para los
// estados completos, y bajamos la saturación de todos los tonos del
// gradiente para que comuniquen progreso sin "gritar".
const PALETTE = {
  complete: '#9ec5a8',    // sage suave (reemplaza verde fluo)
  high: '#c9b878',        // dorado-oliva
  mid: 'var(--accent-gold)',
  low: '#d49a6a',         // tan cálido (reemplaza naranja chillón)
  veryLow: '#b87171',     // rojo desaturado (reemplaza rojo puro)
};

const TYPE_COLOR = {
  player: '#7ba7d4',      // azul desaturado
  badge: 'var(--accent-gold)',
  team_photo: '#a89cc9',  // lila suave
  special: '#9ec5a8',     // sage (mismo que complete)
};

/**
 * Stats visuales del álbum para el Resumen:
 *   - Barras horizontales por grupo (cuánto llevas en cada grupo A-L y
 *     en las secciones especiales).
 *   - Donut por tipo (jugadores / escudos / fotos / especiales).
 *
 * Todo SVG inline — sin libs externas. Mobile-first.
 *
 * Props:
 *   - allStickers: array completo de stickers (con group, section, type).
 *   - collected: state.collected.
 *   - duplicates: state.duplicates.
 */
export default function AlbumStatsCharts({ allStickers, collected, duplicates }) {
  const [tab, setTab] = useState('groups'); // 'groups' | 'types'
  // Default colapsado: el widget agrega ruido visual en el Resumen
  // si está siempre abierto. Persistimos la decisión del usuario en
  // localStorage para que no tenga que volver a colapsarlo cada vez.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) !== '0'; }
    catch { return true; }
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0'); }
    catch { /* localStorage lleno — silencioso */ }
  }, [collapsed]);

  const groupStats = useMemo(
    () => computeGroupStats(allStickers, collected, duplicates),
    [allStickers, collected, duplicates]
  );
  const typeStats = useMemo(
    () => computeTypeStats(allStickers, collected, duplicates),
    [allStickers, collected, duplicates]
  );

  if (!allStickers || allStickers.length === 0) return null;

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-color)',
      borderRadius: 14,
      padding: collapsed ? '12px 16px' : '16px',
      marginBottom: 20,
      transition: 'padding 0.2s ease',
    }}>
      <div
        onClick={() => setCollapsed(v => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCollapsed(v => !v); } }}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: collapsed ? 0 : 14,
          flexWrap: 'wrap',
          gap: 8,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <h3 style={{
          margin: 0,
          fontSize: '0.95rem',
          color: 'var(--text-primary)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontWeight: 600,
        }}>
          📈 Desglose visual
        </h3>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
          {!collapsed && (
            <>
              <TabButton active={tab === 'groups'} onClick={() => setTab('groups')}>Por grupo</TabButton>
              <TabButton active={tab === 'types'} onClick={() => setTab('types')}>Por tipo</TabButton>
            </>
          )}
          <span style={{
            fontSize: '0.72rem',
            color: 'var(--text-muted)',
            marginLeft: 6,
            transition: 'transform 0.2s',
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)',
            display: 'inline-block',
          }}>▼</span>
        </div>
      </div>

      {!collapsed && tab === 'groups' && <GroupBars stats={groupStats} />}
      {!collapsed && tab === 'types' && <TypeDonut stats={typeStats} />}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="btn"
      style={{
        padding: '5px 12px',
        fontSize: '0.78rem',
        background: active ? 'var(--accent-gold)' : 'rgba(255,255,255,0.05)',
        color: active ? '#1a1a1a' : 'var(--text-secondary)',
        border: '1px solid ' + (active ? 'var(--accent-gold)' : 'var(--border-color)'),
        fontWeight: active ? 700 : 500,
      }}
    >
      {children}
    </button>
  );
}

function GroupBars({ stats }) {
  if (stats.length === 0) return <Empty>Sin datos para mostrar.</Empty>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {stats.map(g => <BarRow key={g.id} row={g} />)}
    </div>
  );
}

function BarRow({ row }) {
  const complete = row.pct === 100;
  // Color: gradiente sage→dorado→rojo desaturado según pct. La paleta
  // es deliberadamente más apagada que la original — evita el verde
  // fluo y los tonos chillones, queda alineado con la estética dorada
  // del resto de la app.
  const barColor = complete
    ? PALETTE.complete
    : row.pct >= 75 ? PALETTE.high
    : row.pct >= 50 ? PALETTE.mid
    : row.pct >= 25 ? PALETTE.low
    : PALETTE.veryLow;

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        fontSize: '0.78rem',
        marginBottom: 3,
      }}>
        <span style={{
          color: row.isSection ? 'var(--text-muted)' : 'var(--text-primary)',
          fontWeight: row.isSection ? 500 : 600,
          fontStyle: row.isSection ? 'italic' : 'normal',
        }}>
          {complete && '✓ '}{row.name}
        </span>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>
          {row.have}/{row.total} · <strong style={{ color: barColor }}>{row.pct}%</strong>
        </span>
      </div>
      <div style={{
        background: 'rgba(255,255,255,0.06)',
        borderRadius: 4,
        height: 6,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${row.pct}%`,
          height: '100%',
          background: barColor,
          borderRadius: 4,
          transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  );
}

function TypeDonut({ stats }) {
  if (stats.length === 0) return <Empty>Sin datos para mostrar.</Empty>;

  const total = stats.reduce((s, t) => s + t.total, 0);
  const have = stats.reduce((s, t) => s + t.have, 0);
  const overallPct = total > 0 ? Math.round((have / total) * 100) : 0;

  // Colores por tipo: usa TYPE_COLOR (paleta desaturada, top del módulo).
  const COLOR = TYPE_COLOR;

  // Donut: una rebanada por tipo, proporcional a su TOTAL en el álbum
  // (no a "have"). Adentro mostramos el % global de progreso.
  // La rebanada se llena del color del tipo según el % de "have" de
  // ESE tipo — esto comunica dos dimensiones a la vez.
  const radius = 70;
  const stroke = 16;
  const circ = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div style={{
      display: 'flex',
      gap: 16,
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'center',
    }}>
      <div style={{ position: 'relative', width: 180, height: 180, flexShrink: 0 }}>
        <svg width="180" height="180" viewBox="0 0 180 180" style={{ transform: 'rotate(-90deg)' }}>
          {stats.map((t, i) => {
            const fraction = total > 0 ? t.total / total : 0;
            const len = fraction * circ;
            const gap = circ - len;
            const fillRatio = t.pct / 100;
            const fillLen = len * fillRatio;
            const baseColor = COLOR[t.type] || 'var(--accent-gold)';
            const el = (
              <g key={t.type}>
                {/* Track (color tenue del tipo) */}
                <circle
                  cx="90" cy="90" r={radius}
                  fill="none"
                  stroke={baseColor}
                  strokeOpacity={0.18}
                  strokeWidth={stroke}
                  strokeDasharray={`${len} ${gap}`}
                  strokeDashoffset={-offset}
                />
                {/* Filled portion del tipo */}
                <circle
                  cx="90" cy="90" r={radius}
                  fill="none"
                  stroke={baseColor}
                  strokeWidth={stroke}
                  strokeDasharray={`${fillLen} ${circ - fillLen}`}
                  strokeDashoffset={-offset}
                  style={{ transition: 'stroke-dasharray 0.5s ease' }}
                />
              </g>
            );
            offset += len;
            return el;
          })}
        </svg>
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--accent-gold)', lineHeight: 1 }}>
            {overallPct}%
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {have}/{total}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 160 }}>
        {stats.map(t => (
          <div key={t.type} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              width: 10, height: 10, borderRadius: 2,
              background: COLOR[t.type] || 'var(--accent-gold)',
              flexShrink: 0,
            }} />
            <span style={{ fontSize: '0.82rem', color: 'var(--text-primary)', flex: 1 }}>
              {t.label}
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {t.have}/{t.total} ({t.pct}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Empty({ children }) {
  return (
    <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
      {children}
    </div>
  );
}
